import { Component, Mixins, Watch } from 'vue-property-decorator'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { BBadge } from 'bootstrap-vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import moment, { Moment } from 'moment'
import groupBy from 'lodash/groupBy'
import { emitter, DateMixin, Spinner } from '@kwixeo/interface-vue'

@Component({
  computed: {
    ...mapGetters('user', ['teamUserConfigs', 'can']),
    ...mapGetters('subscriptions', ['hasModule']),
    ...mapState('calendars', ['headerEvents', 'totalHeaderEvents', 'headerEventsLoaded'])
  },
  methods: {
    ...mapActions('calendars', ['loadHeaderEvents', 'setHeaderEvent', 'deleteHeaderEvent'])
  },
  components: {
    FontAwesomeIcon,
    BBadge,
    Spinner
  }
})
export default class CalendarHeader extends Mixins(DateMixin) {
  teamUserConfigs!: KwixeoFramework.ConfigAttributes
  hasModule!: (key: string) => boolean
  can!: (key: string, level: number) => boolean
  loadHeaderEvents!: (force?: boolean) => void
  setHeaderEvent!: (event: KwixeoCalendars.Event) => void
  deleteHeaderEvent!: (event: KwixeoCalendars.Event) => void
  headerEvents!: KwixeoCalendars.Events
  totalHeaderEvents!: number
  headerEventsLoaded!: boolean

  private loading = false

  async created (): Promise<void> {
    if (this.calendarIsActive && this.can('calendar', 1)) {
      await this.loadEvents()
    }
  }

  get calendarIsActive (): boolean {
    return this.hasModule('calendar')
  }

  @Watch('calendarIsActive')
  private async watchCalendarIsActive (): Promise<void> {
    if (this.calendarIsActive) {
      await this.loadEvents(true)
    }
  }

  mounted (): void {
    emitter.on('eventCreated', this.eventCreated)
    emitter.on('eventUpdated', this.eventUpdated)
    emitter.on('eventDeleted', this.eventDeleted)
  }

  destroyed (): void {
    emitter.off('eventCreated', this.eventCreated)
    emitter.off('eventUpdated', this.eventUpdated)
    emitter.off('eventDeleted', this.eventDeleted)
  }

  get since (): Moment {
    return moment()
  }

  get until (): Moment {
    return moment().add(this.teamUserConfigs.calendar_header_limit, 'days')
  }

  get moreCalendar (): number {
    return this.totalHeaderEvents - this.headerEvents.length
  }

  get groupedEvents (): { [day: string]: KwixeoCalendars.Events} {
    return groupBy(this.headerEvents, event => moment(event.begin).format('YYYY-MM-DD'))
  }

  get eventTitle (): (title: string) => string {
    return (title: string) => title ?? this.$t('calendars.event.noTitle')
  }

  get eventTime (): (datetime: string) => string {
    return (datetime: string) => moment(datetime).format('HH:mm')
  }

  private eventCreated (event: KwixeoCalendars.Event): void {
    const compare = moment(event.begin)
    if (compare.isSameOrAfter(this.since, 'day') && compare.isSameOrBefore(this.until, 'day')) {
      this.setHeaderEvent(event)
    }
  }
  private eventUpdated (evt: KwixeoCalendars.EventEmitterEvent): void {
    const event = evt.new
    const index = this.headerEvents.findIndex(val => val.id === event.id)
    if (index >= 0) {
      this.eventDeleted(event)
    }
    this.eventCreated(event)
  }

  private eventDeleted (event: KwixeoCalendars.Event): void {
    const index = this.headerEvents.findIndex(val => val.id === event.id)
    if (index >= 0) {
      this.deleteHeaderEvent(event)
    }
  }

  @Watch('headerEventsLoaded')
  private async watchCalendarsLoaded (state: boolean, old: boolean): Promise<void> {
    if (!state && old) {
      await this.loadEvents()
    }
  }

  private async loadEvents (force?: boolean): Promise<void> {
    this.loading = true
    await this.loadHeaderEvents(force)
    this.loading = false
  }
}
